import React, { useState, useEffect } from 'react';
import CountUp from "react-countup";
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, Col } from 'reactstrap';
import axios from 'axios';
import { useAuth } from '../../../Provider/AuthProvider';

interface DateFilter {
    type?: 'day' | 'month' | 'year' | 'period';
    value?: string | number | Date;
    start?: Date;
    end?: Date;
}

interface WidgetsProps {
    dateFilter?: DateFilter;
}

// Interfaces pour la réponse API allcountvend
interface AllcountvendResponse {
    success: boolean;
    message: string;
    data: AllcountvendData;
    context: AllcountvendContext;
}

interface AllcountvendData {
    customer_orders: {
        total_count: number;
        by_status: {
            draft: number;
            validated: number;
            closed: number;
            cancelled: number;
        };
    };
    invoices: {
        total_count: number;
        paid: {
            count: number;
            amount: number;
            formatted: string;
        };
        unpaid: {
            count: number;
            amount: number;
            formatted: string;
        };
        total_amount: number;
        total_formatted: string;
        paid_percentage?: number;
        unpaid_percentage?: number;
    };
    orders_to_validate: {
        count: number;
        details: {
            status_validated: number;
            delivery_null: number;
            validation_flag: number;
            user_author: number;
        };
    };
    propals: {
        total_count: number;
        by_status: {
            draft: number;
            validated: number;
            signed: number;
            not_signed: number;
            processed: number;
            cancelled: number;
        };
        needing_attention: number;
    };
    summary: {
        total_documents: number;
        total_invoice_amount: number;
        total_invoice_formatted: string;
        pending_orders_to_validate: number;
        total_propals: number;
        propals_needing_attention: number;
    };
}

interface AllcountvendContext {
    entity: number;
    user: number;
    date_filter: {
        type: string;
        parameters: {
            filter_type: string;
            filter_value: string;
            date_start: string;
            date_end: string;
        };
        description?: string;
    };
    entityname: string;
}

const WidgetsVend = ({ dateFilter }: WidgetsProps) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    
    // Structure de données unifiée pour allcountvend
    const [data, setData] = useState<AllcountvendData>({
        customer_orders: {
            total_count: 0,
            by_status: {
                draft: 0,
                validated: 0,
                closed: 0,
                cancelled: 0
            }
        },
        invoices: {
            total_count: 0,
            paid: {
                count: 0,
                amount: 0,
                formatted: '0,00 €'
            },
            unpaid: {
                count: 0,
                amount: 0,
                formatted: '0,00 €'
            },
            total_amount: 0,
            total_formatted: '0,00 €',
            paid_percentage: 0,
            unpaid_percentage: 0
        },
        orders_to_validate: {
            count: 0,
            details: {
                status_validated: 0,
                delivery_null: 0,
                validation_flag: 0,
                user_author: 0
            }
        },
        propals: {
            total_count: 0,
            by_status: {
                draft: 0,
                validated: 0,
                signed: 0,
                not_signed: 0,
                processed: 0,
                cancelled: 0
            },
            needing_attention: 0
        },
        summary: {
            total_documents: 0,
            total_invoice_amount: 0,
            total_invoice_formatted: '0,00 €',
            pending_orders_to_validate: 0,
            total_propals: 0,
            propals_needing_attention: 0
        }
    });

    // Fonction pour formater la date
    const formatDate = (date: string | number | Date | undefined): string => {
        if (!date) return '';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Fonction pour construire les paramètres de filtre de date
    const buildDateFilterParams = () => {
        if (!dateFilter) return {};

        switch (dateFilter.type) {
            case 'day':
                return {
                    filter_type: 'day',
                    filter_value: formatDate(dateFilter.value)
                };
            case 'month':
                return {
                    filter_type: 'month',
                    filter_value: dateFilter.value
                };
            case 'year':
                return {
                    filter_type: 'year',
                    filter_value: dateFilter.value
                };
            case 'period':
                return {
                    filter_type: 'period',
                    date_start: formatDate(dateFilter.start),
                    date_end: formatDate(dateFilter.end)
                };
            default:
                return {};
        }
    };

    const fetchAllData = async () => {
        if (!user?.entity) return;
        
        setLoading(true);
        
        try {
            const params: any = {
                action: 'allcountvend',
                entity: user.entity
            };

            // Ajouter les filtres de date si fournis
            const dateFilters = buildDateFilterParams();
            Object.assign(params, dateFilters);

            console.log('📤 Paramètres allcountvend:', params);

            const response:any = await axios.post<AllcountvendResponse>(
                'https://api.apapiecesauto.fr/apiV2/',
                params,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.data && response.success) {
                const apiData = response.data;
                
                // Calculer les pourcentages si non fournis
                const paidPercentage = apiData.invoices.paid_percentage || 
                    (apiData.invoices.total_count > 0 
                        ? (apiData.invoices.paid.count / apiData.invoices.total_count) * 100 
                        : 0);
                const unpaidPercentage = apiData.invoices.unpaid_percentage || 
                    (apiData.invoices.total_count > 0 
                        ? (apiData.invoices.unpaid.count / apiData.invoices.total_count) * 100 
                        : 0);

                setData({
                    customer_orders: {
                        total_count: apiData.customer_orders?.total_count || 0,
                        by_status: {
                            draft: apiData.customer_orders?.by_status?.draft || 0,
                            validated: apiData.customer_orders?.by_status?.validated || 0,
                            closed: apiData.customer_orders?.by_status?.closed || 0,
                            cancelled: apiData.customer_orders?.by_status?.cancelled || 0
                        }
                    },
                    invoices: {
                        total_count: apiData.invoices?.total_count || 0,
                        paid: {
                            count: apiData.invoices?.paid?.count || 0,
                            amount: apiData.invoices?.paid?.amount || 0,
                            formatted: apiData.invoices?.paid?.formatted || '0,00 €'
                        },
                        unpaid: {
                            count: apiData.invoices?.unpaid?.count || 0,
                            amount: apiData.invoices?.unpaid?.amount || 0,
                            formatted: apiData.invoices?.unpaid?.formatted || '0,00 €'
                        },
                        total_amount: apiData.invoices?.total_amount || 0,
                        total_formatted: apiData.invoices?.total_formatted || '0,00 €',
                        paid_percentage: Math.round(paidPercentage * 100) / 100,
                        unpaid_percentage: Math.round(unpaidPercentage * 100) / 100
                    },
                    orders_to_validate: {
                        count: apiData.orders_to_validate?.count || 0,
                        details: {
                            status_validated: apiData.orders_to_validate?.details?.status_validated || 0,
                            delivery_null: apiData.orders_to_validate?.details?.delivery_null || 0,
                            validation_flag: apiData.orders_to_validate?.details?.validation_flag || 0,
                            user_author: apiData.orders_to_validate?.details?.user_author || 0
                        }
                    },
                    propals: {
                        total_count: apiData.propals?.total_count || 0,
                        by_status: {
                            draft: apiData.propals?.by_status?.draft || 0,
                            validated: apiData.propals?.by_status?.validated || 0,
                            signed: apiData.propals?.by_status?.signed || 0,
                            not_signed: apiData.propals?.by_status?.not_signed || 0,
                            processed: apiData.propals?.by_status?.processed || 0,
                            cancelled: apiData.propals?.by_status?.cancelled || 0
                        },
                        needing_attention: apiData.propals?.needing_attention || 0
                    },
                    summary: {
                        total_documents: apiData.summary?.total_documents || 0,
                        total_invoice_amount: apiData.summary?.total_invoice_amount || 0,
                        total_invoice_formatted: apiData.summary?.total_invoice_formatted || '0,00 €',
                        pending_orders_to_validate: apiData.summary?.pending_orders_to_validate || 0,
                        total_propals: apiData.summary?.total_propals || 0,
                        propals_needing_attention: apiData.summary?.propals_needing_attention || 0
                    }
                });

                console.log('✅ Données allcountvend reçues:', apiData);
                console.log('📊 Contexte:', response.data.context);
            } else {
                console.error('❌ Erreur API:', response.data?.message);
                resetData();
            }
        } catch (error) {
            console.error('❌ Erreur lors de l\'appel allcountvend:', error);
            resetData();
        } finally {
            setLoading(false);
        }
    };

    // Fonction pour réinitialiser les données en cas d'erreur
    const resetData = () => {
        setData({
            customer_orders: {
                total_count: 0,
                by_status: {
                    draft: 0,
                    validated: 0,
                    closed: 0,
                    cancelled: 0
                }
            },
            invoices: {
                total_count: 0,
                paid: {
                    count: 0,
                    amount: 0,
                    formatted: '0,00 €'
                },
                unpaid: {
                    count: 0,
                    amount: 0,
                    formatted: '0,00 €'
                },
                total_amount: 0,
                total_formatted: '0,00 €',
                paid_percentage: 0,
                unpaid_percentage: 0
            },
            orders_to_validate: {
                count: 0,
                details: {
                    status_validated: 0,
                    delivery_null: 0,
                    validation_flag: 0,
                    user_author: 0
                }
            },
            propals: {
                total_count: 0,
                by_status: {
                    draft: 0,
                    validated: 0,
                    signed: 0,
                    not_signed: 0,
                    processed: 0,
                    cancelled: 0
                },
                needing_attention: 0
            },
            summary: {
                total_documents: 0,
                total_invoice_amount: 0,
                total_invoice_formatted: '0,00 €',
                pending_orders_to_validate: 0,
                total_propals: 0,
                propals_needing_attention: 0
            }
        });
    };

    // ============================================
    // Fonctions de navigation
    // ============================================
    const handleNavigateToPropals = () => {
        navigate('/devis');
    };

    const handleNavigateToCustomerOrders = () => {
        navigate('/commandes');
    };

    const handleNavigateToInvoices = () => {
        navigate('/factures');
    };

    const handleNavigateToOrdersToValidate = () => {
        navigate('/commandes', {
    state: {
      validatedWithExpeditions: true  
    }
  });
    };

    // Charger les données au montage du composant et quand les filtres changent
    useEffect(() => {
        fetchAllData();
    }, [user?.entity, JSON.stringify(dateFilter)]);

    // ============================================
    // Widgets avec toutes les données dynamiques
    // ============================================
    const vendWidgets = [
        {
            id: 1,
            label: "Commandes clients",
            badgeClass: data.customer_orders.total_count > 0 ? "success" : "danger",
            badge: data.customer_orders.total_count > 0 ? "ri-arrow-up-line" : "ri-arrow-down-line",
            percentage: data.customer_orders.total_count > 0 ? "100" : "0",
            prefix: "",
            suffix: "",
            separator: "",
            counter: data.customer_orders.total_count,
            decimals: 0,
            link: "Voir commandes clients",
            onClick: handleNavigateToCustomerOrders,
            bgcolor: "primary",
            icon: "ri-shopping-cart-2-line",
            loading: loading,
            tooltip: `Brouillon: ${data.customer_orders.by_status.draft} | Validé: ${data.customer_orders.by_status.validated} | Clôturé: ${data.customer_orders.by_status.closed} | Annulé: ${data.customer_orders.by_status.cancelled}`
        },
        {
            id: 2,
            label: "Factures",
            badgeClass: data.invoices.paid_percentage && data.invoices.paid_percentage > 50 ? "success" : "warning",
            badge: data.invoices.paid_percentage && data.invoices.paid_percentage > 50 ? "ri-arrow-up-line" : "ri-arrow-down-line",
            percentage: data.invoices.paid_percentage?.toFixed(1) || "0",
            prefix: "",
            suffix: "",
            separator: "",
            counter: data.invoices.total_count,
            decimals: 0,
            link: "Voir factures",
            onClick: handleNavigateToInvoices,
            bgcolor: "danger",
            icon: "ri-file-text-line",
            loading: loading
           
        },
        {
            id: 3,
            label: "Commandes à valider",
            badgeClass: data.orders_to_validate.count >= 0 ? "warning" : "success",
            badge: data.orders_to_validate.count >= 0 ? "ri-alert-line" : "ri-check-line",
            percentage: data.orders_to_validate.count > 0 ? "Action requise" : "0",
            prefix: "",
            suffix: "",
            separator: "",
            counter: data.orders_to_validate.count,
            decimals: 0,
            link: "Voir commandes à valider",
            onClick: handleNavigateToOrdersToValidate,
            bgcolor: "warning",
            icon: "ri-time-line",
            loading: loading,
            tooltip: `En attente de validation: ${data.orders_to_validate.count}`
        },
        {
            id: 4,
            label: "Devis",
            badgeClass: data.propals.needing_attention > 0 ? "warning" : "success",
            badge: data.propals.needing_attention > 0 ? "ri-alert-line" : "ri-check-line",
            percentage: data.propals.total_count > 0 
                ? ((data.propals.needing_attention / data.propals.total_count) * 100).toFixed(1)
                : "0",
            prefix: "",
            suffix: "",
            separator: "",
            counter: data.propals.total_count,
            decimals: 0,
            link: "Voir devis",
            onClick: handleNavigateToPropals,
            bgcolor: "info",
            icon: "ri-file-copy-line",
            loading: loading,
            tooltip: `À traiter: ${data.propals.needing_attention} | Total: ${data.propals.total_count}`
        }
    ];

    return (
        <React.Fragment>
            {vendWidgets.map((item, key) => (
                <Col xl={3} md={6} key={key}>
                    <Card className="card-animate" title={item.tooltip}>
                        <CardBody>
                            <div className="d-flex align-items-center">
                                <div className="flex-grow-1 overflow-hidden">
                                    <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                                        {item.label}
                                        {item.loading && <span className="ms-2 spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
                                    </p>
                                </div>
                                <div className="flex-shrink-0">
                                    <h5 className={"fs-14 mb-0 text-" + item.badgeClass}>
                                        {item.badge ? <i className={"fs-13 align-middle " + item.badge}></i> : null} 
                                        {typeof item.percentage === 'string' && item.percentage.includes('Action') 
                                            ? item.percentage 
                                            : `${item.percentage} %`}
                                    </h5>
                                </div>
                            </div>
                            <div className="d-flex align-items-end justify-content-between mt-4">
                                <div>
                                    <h4 className="fs-20 fw-semibold ff-secondary mb-4">
                                        <span className="counter-value">
                                            {item.loading ? (
                                                <span className="text-muted">...</span>
                                            ) : (
                                                <CountUp
                                                    start={0}
                                                    prefix={item.prefix}
                                                    suffix={item.suffix}
                                                    separator={item.separator}
                                                    end={item.counter}
                                                    decimals={item.decimals}
                                                    duration={4}
                                                />
                                            )}
                                        </span>
                                    </h4>
                                    
                                    <button 
                                        onClick={item.onClick} 
                                        className="text-decoration-underline btn btn-link p-0"
                                        style={{ border: 'none', background: 'none', color: '#0d6efd', cursor: 'pointer' }}
                                    >
                                        {item.link}
                                    </button>
                                </div>
                                <div className="avatar-sm flex-shrink-0">
                                    <span className={"avatar-title rounded fs-3 bg-" + item.bgcolor + "-subtle"}>
                                        <i className={`text-${item.bgcolor} ${item.icon}`}></i>
                                    </span>
                                </div>
                            </div>
                            
                            
                            
                     
            
                        </CardBody>
                    </Card>
                </Col>
            ))}
        </React.Fragment>
    );
};

export default WidgetsVend;